import { DataPage } from '@/app.routes';
import { UserHelper } from '@/helpers/user.helper';
import { EEMO_ICON_KEY } from '@/modules/icon-eemo-module/enums/eemo-icon.enums';
import { AppConfigService } from '@/services/app-config.service';
import { MenuToNativeId } from '@/services/bridge/bridge.service';
import { NavService } from '@/services/nav.service';
import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { BonusWallet, CertificationRequest, RealProperty, SharingDataCategory } from '@eemo/api-client';
import { Subscription, filter } from 'rxjs';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { CertificationHelper } from '@/helpers/certification.helper';
import { ActionService } from '@/services/action.service';
import { NotificationCheckService } from '@/services/notification-check.service';
import { ReferralStateService } from '@/services/referral-state.service';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

interface ButtonData {
  icon: string;
  text: string;
  type: ButtonTypeClick;
  link?: string;
  chip?: string;
  chipText?: string;
  disabled?: boolean;
  tooltip?: string;
}

export type ButtonTypeClick =
  | 'uploadDoc'
  | 'scanDoc'
  | 'uploadPhoto'
  | 'addEquipment'
  | 'addEvent'
  | 'addConsumptions'
  | 'addMaintenance'
  | 'addWork'
  | 'addPropertySale'
  | 'addPropertyService'
  | 'addCustomPropertyService'
  | 'showPhotos'
  | 'showQrCode'
  | 'addShare'
  | 'certify'
  | 'confirmCertificationRequest'
  | 'addProperty'
  | 'openHelp'
  | 'goToHome'
  | 'goToAdmin'
  | 'goToKpi'
  | 'goToProfile'
  | 'goToLink';

@Component({
  selector: 'app-float-menu',
  templateUrl: './float-menu.component.html',
  styleUrls: ['./float-menu.component.scss'],
  imports: [
    MatIconModule,
    MatButtonModule,
    NgTemplateOutlet,
    MatMenuModule,
    TooltipDirective,
    MatBadgeModule,
    CurrencyPipe,
  ],
})
export class FloatMenuComponent implements OnInit, OnChanges, OnDestroy {
  private userHelper = inject(UserHelper);
  private navService = inject(NavService);
  private appConfigService = inject(AppConfigService);
  private actionService = inject(ActionService);
  private notificationCheckService = inject(NotificationCheckService);
  private referralState = inject(ReferralStateService);

  prop: RealProperty;
  certificationRequest: CertificationRequest;
  isCertificationRequestOnGoing = false;
  availableShortcuts: { [key: string]: ButtonData } = {};

  readonly SEND_ICON_KEY = EEMO_ICON_KEY.SEND;
  private subs: Subscription[] = [];
  menuEnabled = true;
  canAddProperty = false;
  canUpdateProperty = false;
  canCertifyProperty = false;
  canRequestCertification = false;
  canAddShare = false;
  canViewPhoto = false;
  hasReachedPropertyLimit = false;
  currentPage: DataPage;
  PAGE_ID = MenuToNativeId;
  isMobileAppIOS = false;
  canShowPropertySaleBtnAdd = false;
  canShowPropertyServiceBtnAdd = false;
  canShowCustomPropertyServiceBtnAdd = false;

  mainBtnType: 'addProperty' | 'addInProperty' | 'custom';
  mainBtn: ButtonData | null;
  leftBtn: ButtonData | null;
  rightBtn: ButtonData | null;
  shortcuts: ButtonData[];
  /** referral bonus wallet of an agent, shown on the "Mon compte" button when its balance is > 0 */
  wallet: BonusWallet | null = null;

  ngOnInit(): void {
    this.canAddProperty = this.userHelper.canAddProperty();
    this.isMobileAppIOS = this.appConfigService.isOSiOS;

    this.availableShortcuts.showPhotos = {
      icon: 'photo_library',
      text: 'Voir les photos',
      type: 'showPhotos',
    };
    this.availableShortcuts.showQrCode = {
      icon: 'qr_code_2',
      text: 'Voir le QR Code',
      type: 'showQrCode',
    };
    this.availableShortcuts.addShare = {
      icon: 'share',
      text: 'Partager le bien',
      type: 'addShare',
    };
    this.availableShortcuts.scanDoc = {
      icon: 'document_scanner',
      text: 'Scanner',
      type: 'scanDoc',
    };
    this.availableShortcuts.certify = {
      icon: this.prop?.certified ? 'update' : 'check_circle_outline',
      text: this.prop?.certified ? 'Renouveler la certification' : 'Certifier le bien',
      type: 'certify',
    };
    this.availableShortcuts.confirmCertificationRequest = {
      icon: 'verified_user',
      text: 'Demander la certification du bien',
      type: 'confirmCertificationRequest',
    };
    this.availableShortcuts.openHelp = {
      icon: 'live_help',
      text: 'Aide',
      type: 'openHelp',
    };
    this.availableShortcuts.goToHome = {
      icon: 'maps_home_work',
      text: 'Mes propriétés',
      type: 'goToHome',
    };
    this.availableShortcuts.goToAdmin = {
      icon: 'apps',
      text: 'Administration',
      type: 'goToAdmin',
    };
    this.availableShortcuts.goToKpi = {
      icon: 'trending_up',
      text: 'KPI',
      type: 'goToKpi',
    };
    this.availableShortcuts.addProperty = {
      icon: 'home',
      text: 'Ajouter un nouveau bien',
      type: 'addProperty',
    };
    this.availableShortcuts.goToProfile = {
      icon: 'person',
      text: 'Mon compte',
      type: 'goToProfile',
    };

    this.subs.push(this.appConfigService.floatMenuEnabledObs.subscribe((b) => (this.menuEnabled = b)));
    this.subs.push(
      this.appConfigService.selectedPropObs.subscribe((prop) => {
        this.prop = prop;
        this.loadPropRights();
        this.availableShortcuts.addProperty.tooltip = this.hasReachedPropertyLimit
          ? "Un compte gratuit n'autorise la gestion que d'un seul bien"
          : 'Ajouter un nouveau bien';
      }),
    );
    this.subs.push(
      this.navService.currentDataPage$.subscribe((data: DataPage) => {
        this.onDataPageChange(data);
      }),
    );
    this.subs.push(
      this.appConfigService.selectedPropCertificationRequestObs.subscribe((r) => {
        this.certificationRequest = r;
        this.isCertificationRequestOnGoing = r && CertificationHelper.isOnGoingCertificationRequest(r);
        this.onDataPageChange(this.currentPage);

        this.canRequestCertification =
          this.canUpdateProperty &&
          !this.canCertifyProperty &&
          !this.prop.certified &&
          !this.prop.archived &&
          !this.isCertificationRequestOnGoing;
      }),
    );
    this.subs.push(
      this.appConfigService.userReachedPropertyLimitObs.subscribe((b) => (this.hasReachedPropertyLimit = b)),
    );
    this.subs.push(this.notificationCheckService.dropBoxCount.subscribe(() => this.onDataPageChange(this.currentPage)));
    if (this.userHelper.isRealEstate()) {
      this.subs.push(this.referralState.summary$.subscribe((summary) => (this.wallet = summary?.wallet ?? null)));
      // a credit of the wallet comes with a notification: the summary is reloaded when the count changes
      this.subs.push(
        this.notificationCheckService.notificationCount
          .pipe(filter((count) => count !== null))
          .subscribe(() => this.referralState.refresh()),
      );
    }
  }

  get hasWalletBalance(): boolean {
    return this.wallet?.balance > 0;
  }

  loadPropRights() {
    this.canShowPropertySaleBtnAdd = this.prop && this.userHelper.canAddPropertySale(this.prop);
    this.canShowPropertyServiceBtnAdd = this.prop && this.userHelper.canManagePropertyService(this.prop);
    this.canShowCustomPropertyServiceBtnAdd = this.prop && this.userHelper.canUpdateProperty(this.prop);
    this.canUpdateProperty = this.prop && this.userHelper.canUpdateProperty(this.prop);
    this.canCertifyProperty = this.prop && !this.prop.archived && this.userHelper.canCertifyProperty(this.prop);
    this.canAddShare = this.canUpdateProperty && this.prop.certified && !this.prop.archived;
    this.canViewPhoto = this.prop && this.userHelper.canViewCategory(this.prop, SharingDataCategory.PHOTO);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.certificationRequest?.currentValue) {
      if (this.currentPage) {
        this.onDataPageChange(this.currentPage);
      }
    } else if (changes.prop?.currentValue) {
      this.canShowPropertySaleBtnAdd = this.userHelper.canAddPropertySale(changes.prop.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onDataPageChange(data: DataPage) {
    if (data) {
      this.currentPage = data;
      this.buildMainButton(data);
      this.buildRightLeftButtons(data);
      this.buildShortcuts();
    }
  }

  buildMainButton(data: DataPage) {
    if ([MenuToNativeId.HOME_PROPERTY].includes(data?.page)) {
      this.mainBtnType = 'addProperty';
    } else if (this.prop) {
      // on property page
      this.mainBtnType = 'addInProperty';
    } else {
      this.mainBtnType = 'custom';
    }
  }

  buildRightLeftButtons(data: DataPage) {
    const isAdmin = this.userHelper.isAdmin();
    // default buttons
    this.leftBtn = isAdmin ? this.availableShortcuts.goToAdmin : this.availableShortcuts.goToProfile;
    this.rightBtn = this.availableShortcuts.goToHome;
    if ([MenuToNativeId.HOME_PROPERTY].includes(data?.page)) {
      const dropbox = this.navService.getNavEnvById(MenuToNativeId.DROPBOX);
      if (dropbox.chip) {
        // available docs, the dropbox link is shown
        this.rightBtn = this.buildBtn(dropbox.icon, dropbox.title, 'goToLink', dropbox.link);
        this.rightBtn.chip = dropbox.chip;
        this.rightBtn.chipText = dropbox.chipText;
      } else {
        // the shares link is shown
        const shares = this.navService.getNavEnvById(MenuToNativeId.SHARES);
        this.rightBtn = this.buildBtn(shares.icon, shares.title, 'goToLink', shares.link);
      }
    } else if (this.prop) {
      // on property page
      this.leftBtn =
        this.canUpdateProperty && this.prop.certified
          ? this.availableShortcuts.addShare
          : this.availableShortcuts.showPhotos;
      if (this.isMobileAppIOS && this.canUpdateProperty) {
        this.rightBtn = this.availableShortcuts.scanDoc;
      } else {
        const vault = this.navService.getNavEnvById(MenuToNativeId.VAULT);
        this.rightBtn = this.buildBtn(vault.icon, vault.title, 'goToLink', vault.link);
      }
    }
    // the left button is always help
    //this.leftBtn = this.availableShortcuts.openHelp;
  }

  buildShortcuts() {
    const shortcuts: ButtonData[] = [];

    // shortcuts for property (uid)
    if (this.currentPage?.uid) {
      if (this.canViewPhoto) {
        // photos
        shortcuts.push({
          icon: 'photo_library',
          text: 'Voir les photos',
          type: 'showPhotos',
        });
      }
      // qr code
      if (!this.canUpdateProperty) {
        shortcuts.push({
          icon: 'qr_code_2',
          text: 'Voir le QR Code',
          type: 'showQrCode',
        });
      }
      // share
      if (this.canAddShare) {
        shortcuts.push({
          icon: 'share',
          text: 'Partager le bien',
          type: 'addShare',
        });
      }
      // certify
      if (this.canCertifyProperty) {
        shortcuts.push({
          icon: this.prop?.certified ? 'update' : 'check_circle_outline',
          text: this.prop?.certified ? 'Renouveler la certification' : 'Certifier le bien',
          type: 'certify',
        });
      }
      // confirm certification
      if (this.canRequestCertification) {
        shortcuts.push({
          icon: 'verified_user',
          text: 'Demander la certification du bien',
          type: 'confirmCertificationRequest',
        });
      }
    }

    this.shortcuts = shortcuts;
  }

  onBtnClicked(type: ButtonTypeClick, link?: string) {
    // default actions
    if (type === 'openHelp') {
      this.actionService.openHelp();
    } else if (type === 'goToHome') {
      this.actionService.goToHome();
    } else if (type === 'goToAdmin') {
      this.actionService.goToAdmin();
    } else if (type === 'goToProfile') {
      this.actionService.goToProfile();
    } else if (type === 'goToKpi') {
      this.actionService.goToKpi();
    } else if (type === 'goToLink') {
      this.actionService.goToLink(link);
    } else if (type === 'addProperty') {
      this.actionService.addProperty();
    } else if (type === 'uploadDoc') {
      this.actionService.uploadDoc();
    } else if (type === 'scanDoc') {
      this.actionService.uploadDoc(true);
    } else if (type === 'addConsumptions') {
      this.actionService.addConsumptions();
    } else if (type === 'addCustomPropertyService') {
      this.actionService.addCustomPropertyService();
    } else if (type === 'addMaintenance') {
      this.actionService.addMaintenance();
    } else if (type === 'addEquipment') {
      this.actionService.addEquipment();
    } else if (type === 'addEvent') {
      this.actionService.addEvent();
    } else if (type === 'addPropertySale') {
      this.actionService.addPropertySale();
    } else if (type === 'addPropertyService') {
      this.actionService.addPropertyService();
    } else if (type === 'addWork') {
      this.actionService.addWork();
    } else if (type === 'uploadPhoto') {
      this.actionService.uploadPhoto();
    } else if (type === 'showPhotos') {
      this.actionService.showPhotos();
    } else if (type === 'showQrCode') {
      this.actionService.showQrCode();
    } else if (type === 'addShare') {
      this.actionService.addShare();
    } else if (type === 'certify') {
      this.actionService.certify();
    } else if (type === 'confirmCertificationRequest') {
      this.actionService.confirmCertificationRequest();
    }
  }

  buildBtn(icon: string, text: string, type: ButtonTypeClick, link?: string): ButtonData {
    return {
      icon,
      text,
      type,
      link,
    };
  }
}
